import SEOHead from '@/components/SEOHead';

const LAST_UPDATED = 'May 4, 2026';
const CONTACT_EMAIL = 'trust@builderslab.net';

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="text-xl font-bold text-white mb-4 pb-3 border-b border-zinc-800">{title}</h2>
      <div className="space-y-4 text-zinc-400 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

export default function AcceptableUsePolicy() {
  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-20">
      <SEOHead
        title="Acceptable Use Policy — BuildersLab"
        description="BuildersLab's Acceptable Use Policy outlines what content and conduct is permitted and prohibited when using our AI website builder."
        ogUrl="https://builderslab.net/AcceptableUsePolicy"
        noIndex={false}
      />
      <div className="max-w-4xl mx-auto px-6">

        <div
         
         
         
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold uppercase tracking-wider mb-6">
            Legal
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">
            Acceptable Use Policy
          </h1>
          <p className="text-zinc-500 text-sm">Last updated: {LAST_UPDATED}</p>
          <p className="text-zinc-400 mt-4 text-base leading-relaxed max-w-2xl">
            BuildersLab provides an AI-powered website builder ("Service"). This Acceptable Use
            Policy ("AUP") defines what you may and may not do with the Service. Violations may
            result in suspension or permanent termination of your account.
          </p>
        </div>

        <div
         
         
         
        >
          <Section title="1. Permitted Uses">
            <p>You may use BuildersLab to generate, edit, and publish websites for any lawful purpose, including:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Personal blogs, portfolios, and creative projects.</li>
              <li>Small business and professional service websites.</li>
              <li>Non-profit and charitable organisation sites.</li>
              <li>E-commerce and product landing pages.</li>
              <li>Educational and informational content.</li>
              <li>Marketing and promotional campaigns for legitimate products or services.</li>
            </ul>
          </Section>

          <Section title="2. Prohibited AI-Generated Content Types">
            <p>
              Because BuildersLab uses AI to generate website code and copy, we hold you and your
              AI prompts to the same standard as any other content you publish. You must not
              instruct or use the AI to generate:
            </p>

            <div className="space-y-6">
              <div>
                <h3 className="text-white font-semibold mb-2">2.1 Illegal Content</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Child sexual abuse material (CSAM) or any content that sexualises minors.</li>
                  <li>Content that facilitates or promotes human trafficking, slavery, or forced labour.</li>
                  <li>Instructions for manufacturing weapons, explosives, or controlled substances.</li>
                  <li>Content that infringes third-party intellectual property rights (copyright, trademark, trade secrets).</li>
                  <li>Defamatory, fraudulent, or deceptive content.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">2.2 Harmful & Dangerous Content</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Content that promotes, glorifies, or incites violence, terrorism, or self-harm.</li>
                  <li>Harassment, bullying, or targeted abuse of identifiable individuals.</li>
                  <li>Content designed to facilitate or encourage suicide or self-injury.</li>
                  <li>Misinformation or disinformation intended to deceive the public (e.g. fabricated medical claims, election interference).</li>
                  <li>Malware, phishing pages, or any content designed to compromise computer security.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">2.3 Discriminatory & Hateful Content</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Content that promotes hatred, discrimination, or violence against any person or group based on race, ethnicity, national origin, religion, gender, sexual orientation, disability, or similar protected characteristics.</li>
                  <li>Content that uses slurs or dehumanising language targeting protected groups.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">2.4 Deceptive & Manipulative Content</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Fake news sites, impersonation of real people or organisations, or websites designed to deceive visitors into believing they are on a legitimate site (phishing, brand spoofing).</li>
                  <li>Dark patterns designed to trick users into unintended purchases or consent.</li>
                  <li>AI-generated deepfakes presented as real, without clear disclosure.</li>
                  <li>Pyramid schemes, multi-level marketing structures that rely on illegal recruitment, or Ponzi schemes.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">2.5 Adult & Explicit Content</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Pornography or sexually explicit content of any kind.</li>
                  <li>Escort services or content that facilitates prostitution.</li>
                  <li>Content that sexualises real individuals without their verified, documented consent.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">2.6 Regulated Industries (Restricted Use)</h3>
                <p>
                  The following content types are permitted only where you comply with all applicable
                  laws and regulations in every jurisdiction where your website is accessible:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Online gambling or betting services (requires licensing).</li>
                  <li>Cannabis or controlled-substance dispensaries (requires licensing).</li>
                  <li>Financial advice, investment solicitation, or securities offerings (requires regulatory authorisation).</li>
                  <li>Medical or health advice that could substitute for professional care (must include appropriate disclaimers).</li>
                  <li>Legal advice (must include disclaimer that content does not constitute legal advice).</li>
                </ul>
              </div>
            </div>
          </Section>

          <Section title="3. Prohibited Conduct">
            <p>In addition to the content restrictions above, you must not:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Use the Service to generate spam or bulk unsolicited communications.</li>
              <li>Attempt to reverse-engineer, scrape, or extract our AI models or training data.</li>
              <li>Use automated tools (bots, scripts) to interact with the Service in a way that places unreasonable load on our infrastructure.</li>
              <li>Share your account credentials or provide access to minors under 13 (or the applicable age of digital consent in your country).</li>
              <li>Circumvent any rate limits, credit controls, or security measures.</li>
              <li>Republish AI-generated content in a way that deliberately misleads readers about its AI origin in contexts where such disclosure is legally required.</li>
            </ul>
          </Section>

          <Section title="4. AI Transparency">
            <p>
              BuildersLab generates websites using large language models. You acknowledge that:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>AI-generated content may be inaccurate, incomplete, or biased — you are responsible for reviewing and correcting it before publishing.</li>
              <li>You should not publish AI-generated medical, legal, or financial advice without expert review.</li>
              <li>Where laws in your jurisdiction require disclosure of AI-generated content, you are responsible for including appropriate notices.</li>
            </ul>
          </Section>

          <Section title="5. Reporting Violations">
            <p>
              If you encounter content generated or hosted via BuildersLab that you believe violates
              this AUP, report it to{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:underline">
                {CONTACT_EMAIL}
              </a>. We investigate all credible reports and will take appropriate action, which may
              include content removal and account termination.
            </p>
          </Section>

          <Section title="6. Enforcement">
            <p>
              BuildersLab reserves the right to:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Remove or disable access to any content or account that violates this AUP at any time without prior notice.</li>
              <li>Suspend or terminate accounts for repeat or severe violations.</li>
              <li>Report illegal content to relevant law enforcement authorities.</li>
              <li>Cooperate with law enforcement investigations as required by applicable law.</li>
            </ul>
            <p>
              We do not guarantee we will detect every policy violation. Our enforcement actions do
              not create any liability on our part for failing to prevent prohibited content.
            </p>
          </Section>

          <Section title="7. Relationship to Other Policies">
            <p>
              This AUP supplements, and should be read alongside, our{' '}
              <a href="/TermsOfUse" className="text-orange-400 hover:underline">Terms of Use</a> and{' '}
              <a href="/PrivacyPolicy" className="text-orange-400 hover:underline">Privacy Policy</a>.
              In the event of conflict, the Terms of Use take precedence, followed by this AUP.
            </p>
          </Section>

          <Section title="8. Updates">
            <p>
              We may update this AUP to address new use cases or legal requirements. Material changes
              will be communicated via email and/or in-app notification at least 14 days before they
              take effect. Continued use after the effective date constitutes acceptance.
            </p>
          </Section>

          <Section title="9. Contact">
            <p>
              Questions or concerns about this policy:{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:underline">
                {CONTACT_EMAIL}
              </a>
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
